import dayjs from "dayjs";
import "dayjs/locale/fr";
import "dayjs/locale/en";
import utc from "dayjs/plugin/utc";
import minMax from "dayjs/plugin/minMax";
import timezone from "dayjs/plugin/timezone";
import {DateFormats} from "../constants/DateConstants";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(minMax);

/**
 * Formate une date string (YYYY-MM-DD) au format français (DD/MM/YYYY) sans conversion timezone.
 * Utilise le parsing et formatage UTC pour éviter les décalages de jour.
 *
 * @param dateString La date au format API (YYYY-MM-DD)
 * @returns La date formatée (DD/MM/YYYY) ou null si invalide
 */
const formatDateStringFr = (dateString: string): string => {
  if (!dateString) return null;
  return dayjs.utc(dateString, DateFormats.apiDate).format(DateFormats.shortDateFr);
};

/**
 * Formate une date string (YYYY-MM-DD) au format long localisé (ex: "Mercredi 06 Mai")
 * sans conversion timezone. Utilise le parsing UTC pour éviter les décalages de jour.
 *
 * @param dateString La date au format API (YYYY-MM-DD)
 * @param locale La locale à utiliser (default: navigateur ou 'fr')
 * @returns La date formatée au format long ou null si invalide
 */
const formatDateStringLong = (dateString: string, locale?: string): string => {
  if (!dateString) return null;
  const userLocale = locale || (navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language) || "fr";
  return dayjs.utc(dateString, DateFormats.apiDate).locale(userLocale).format(DateFormats.longDateWithoutYear);
};

/**
 * Formate un objet Date au format long localisé (ex: "Mercredi 06 Mai")
 * en utilisant le fuseau horaire de Paris pour éviter les décalages de jour.
 *
 * @param date L'objet Date à formater
 * @param locale La locale à utiliser (default: navigateur ou 'fr')
 * @returns La date formatée au format long ou null si invalide
 */
const formatDateObjectLong = (date: Date, locale?: string): string => {
  if (!date) return null;
  const userLocale = locale || (navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language) || "fr";
  return dayjs(date).tz(DateFormats.parisTimezone).locale(userLocale).format(DateFormats.longDateWithoutYear);
};


/**
 * Remplace le new Date() pour avoir une date avec heure française
 */
const today = (): Date => {
  return dayjs.utc().utcOffset(60).toDate();
}

/**
 * Formate un objet Date au format API (YYYY-MM-DD).
 * Utilise le fuseau horaire de Paris pour extraire le jour correct,
 * évitant le décalage de jour quand la date locale (ex: 19/02 00:00 UTC+1)
 * serait convertie au jour précédent en UTC (18/02 23:00 UTC).
 *
 * @param date L'objet Date à formater (si non fourni, utilise today())
 * @returns La date au format YYYY-MM-DD
 */
function formatDateYYYYMMDD(date?: Date): string {
  if (!date) {
    date = today();
  }

  return dayjs(date).tz(DateFormats.parisTimezone).format(DateFormats.apiDate);
}

const datePlusDays = (date: Date, add: number) => {
  if(date === null) {
    return null
  }
  const newDate = new Date(date)
  newDate.setDate(date.getDate() + add);
  return newDate;
};

const dateMinusDays = (date: Date, minus: number) => {
  if(date === null) {
    return null
  }
  const newDate = new Date(date)
  newDate.setDate(date.getDate() - minus);
  return newDate;
};

const parseDate = (value: string): Date => {
  if (!value || value?.trim() == "") {
    return null
  }
  return dayjs.tz(value, DateFormats.parisTimezone).toDate();
};

/**
 * Formate une date au format souhaité en utilisant le format UTC pour éviter les changements de jour en fonction du fuseau horaire UTC+1 (Paris).
 *
 * @param value La valeur de la date (sans heure) à formater.
 * @param formatWanted Le format souhaité de la date. Par défaut : "DD/MM/YYYY".
 * @param currentFormat Le format actuel de la date. Par défaut : "YYYY-MM-DD".
 * @returns La date formatée selon le format spécifié.
 */
const formatDateDayJs = (value: string, formatWanted = DateFormats.shortDateFr, currentFormat = DateFormats.apiDate): string => {
  const userLocale = (navigator.languages && navigator.languages.length ? navigator.languages[0] : navigator.language);

  // Conversion de la date en tenant compte du format spécifié et du fuseau horaire UTC+1 (Paris)
  return value ? dayjs.utc(value, currentFormat).locale(userLocale).utcOffset(60).format(formatWanted) : null;
}


/**
 * Convertit une string de date en objet Date JavaScript.
 * ATTENTION: Pour les dates pures (YYYY-MM-DD), utiliser formatDateStringFr ou formatDateStringLong
 * pour l'affichage afin d'éviter les décalages de timezone.
 *
 * Cette fonction parse les dates en UTC pour éviter les décalages de jour.
 *
 * @param date La date au format string (YYYY-MM-DD ou ISO avec timezone)
 * @returns Un objet Date représentant minuit UTC du jour spécifié
 */
function convertStringToObjectDate(date?: string): Date {
  const regexTimeUTC = /(Z|([+|-][0-9]{2}(:?[0-9]{2})?))$/;
  if (!date) {
    return dayjs.utc().utcOffset(60).toDate();
  }
  // Pour les dates pures (YYYY-MM-DD), parser en UTC pour éviter les décalages de timezone
  if (!regexTimeUTC.test(date)) {
    // Utilise dayjs.utc pour parser la date comme UTC minuit, évitant ainsi les décalages
    return dayjs.utc(date, DateFormats.apiDate).toDate();
  }

  return dayjs.utc(date, DateFormats.apiDate).utcOffset(60).toDate();
}

/**
 *
 * @param dateTime
 *
 * return ex :06/02/2024 14:24
 */
function formatDateToLocalDateTime(dateTime?: string | Date): string {
  if (!dateTime) {
    return ""
  }
  const dateUTC = dayjs(dateTime).utc()
  const dateParis = dateUTC.tz(DateFormats.parisTimezone)

  return dateParis.format(DateFormats.shortDateTimeFr)
}

function formatDuration(totalSeconds?: number): string {
  if (!totalSeconds) {
    return "-";
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds - hours * 3600 - minutes * 60;

  return [`${hours}h`, `${minutes}m`, `${seconds}s`]
    .filter((item) => item[0] !== "0")
    .join(" ");
}

function convertSecondsToTime(seconds?: number): string {
  if (!seconds) {
    return undefined;
  }
  return new Date(seconds * 1000).toISOString().substring(11, 19);
}

function convertTimeToSeconds(time?: string): number {
  if (!time) {
    return undefined;
  }
  const [hours, minutes] = time.split(":");
  return +hours * 3600 + +minutes * 60;
}

const getTimeWithoutSeconds = (time: string): string => {
  if (!time) {
    return undefined;
  }
  const [hours, minutes] = time.split(":");
  return [hours, minutes].join(":");
}

function formatLocalTime(time: string): string {
  if (!time) {
    return "";
  }
  const [hours, minutes] = time.split(":");
  return `${hours}h${minutes}`;
}

const getNumberOfMonthsBetweenTwoDates = (date1: Date, date2: Date): number => {
  const differenceInMonths = date2.getMonth() - date1.getMonth();
  const differenceInYears = date2.getFullYear() - date1.getFullYear();
  return 12 * differenceInYears + differenceInMonths;
};

const getHoursBetweenTwoDates = (date1: Date, date2: Date = today()): number => {
  const differenceEnMilliseconds: number = Math.abs(date2.getTime() - date1.getTime());
  return Math.round(differenceEnMilliseconds / (1000 * 60 * 60));
}

const firstDayOfLastMonth = (): Date => {
  return dayjs(today()).subtract(1, "month").startOf("month").toDate();
};

const lastDayOfLastMonth = (): Date => {
  return dayjs(today()).startOf("month").subtract(1, "day").toDate();
};

const firstDayOfTheMonth = (): Date => {
  const date = today();
  date.setDate(1);
  return date;
}


const lastDayOfTheMonth = (): Date => {
  const date = today();
  date.setMonth(date.getMonth() + 1);
  date.setDate(0);
  return date;
}


const getDaysArrayBetweenDates = (startDate: string, endDate: string): Date[] => {
  const result = [];
  const start = convertStringToObjectDate(startDate);
  const end = convertStringToObjectDate(endDate);

  for(let i = start ; i <= end; i.setDate(i.getDate() + 1)) {
    result.push(new Date(i));
  }
  return result;
};

const getMaxDate = (dates: string[]): string => {
  return dayjs.max(dates.map(date => dayjs(date))).format(DateFormats.apiDate)
}

const getDateOneMonthAgo = () : string => {
  return dayjs().subtract(1, "month").format(DateFormats.apiDate)
}

const isBefore = (date1: string, date2: string): boolean =>{
  return dayjs(date1).isBefore(date2);
}

export const dateUtils = Object.freeze({
  formatDateYYYYMMDD,
  parseDate,
  convertStringToObjectDate,
  formatDateToLocalDateTime,
  formatDuration,
  convertSecondsToTime,
  convertTimeToSeconds,
  getTimeWithoutSeconds,
  formatLocalTime,
  getNumberOfMonthsBetweenTwoDates,
  firstDayOfLastMonth,
  lastDayOfLastMonth,
  firstDayOfTheMonth,
  lastDayOfTheMonth,
  getDaysArrayBetweenDates,
  datePlusDays,
  dateMinusDays,
  getHoursBetweenTwoDates,
  getMaxDate,
  getDateOneMonthAgo,
  formatDateDayJs,
  today,
  isBefore,
  formatDateStringFr,
  formatDateStringLong,
  formatDateObjectLong
});
